<script lang="ts" setup>
import useVuelidate, { ValidationArgs } from '@vuelidate/core';

interface Props {
  rules?: ValidationArgs;
  formData: Record<string, unknown>;
  autocomplete?: boolean;
  disabled?: boolean;
  isActionsSticky?: boolean;
}

defineExpose({ validateAndSubmit, reset });

const props = withDefaults(defineProps<Props>(), {
  rules: () => ({}),
});

const emit = defineEmits<{
  (e: 'formSubmit', p: typeof props.formData): void;
}>();

const v$ = useVuelidate<typeof props.formData>(props.rules, props.formData, {
  $autoDirty: true,
});

const formErrors = computed(() => {
  const errorList: Record<string, string[]> = {};
  v$.value.$errors.forEach((err) => {
    errorList[err.$propertyPath] = [
      typeof err.$message === 'string' ? err.$message : err.$message.value,
    ];
  });

  return errorList;
});

const autocomplete = computed(() => (props.autocomplete ? 'on' : undefined));

function reset() {
  v$.value.$reset();
}

async function validateAndSubmit() {
  if (props.disabled) {
    return;
  }

  await v$.value.$validate();

  if (!v$.value.$error) {
    emit('formSubmit', props.formData);
  }
}
</script>

<template>
  <form
    class="ui-form"
    :autocomplete="autocomplete"
    @submit.prevent="validateAndSubmit"
  >
    <div class="main">
      <slot
        :form-errors="formErrors"
        :form-submit="validateAndSubmit"
        :has-errors="v$.$invalid"
        :is-dirty="v$.$anyDirty"
      />
    </div>

    <div class="actions" :class="{ 'actions-sticky': props.isActionsSticky }">
      <slot
        name="bottom"
        :form-errors="formErrors"
        :form-submit="validateAndSubmit"
        :form-reset="reset"
        :has-errors="v$.$invalid"
        :is-dirty="v$.$anyDirty"
      />
    </div>
  </form>
</template>

<style lang="scss" scoped>
.ui-form {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: var(--space-8);
  width: 100%;
}
.main {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: var(--space-12);
}

.actions {
  flex-grow: 0;
}

.actions-sticky {
  position: sticky;
  bottom: 0px;
}
</style>
